<template>
	<EventLayout :event="event" :subtitle="trans('events.payments.title')">
		<template #actions>
			<template v-if="provider.id > 0">
				<PrimaryButton :color="Color.Green" @click.stop="update">
					<i class="fa-solid fa-check mr-2"/>
					{{ trans('general.update') }}
				</PrimaryButton>
				<PrimaryButton :color="Color.Red" @click.stop="remove">
					<i class="fa-solid fa-trash mr-2"/>
					{{ trans('general.delete') }}
				</PrimaryButton>
			</template>
			<PrimaryButton v-else :color="Color.Green" @click.stop="insert">
				<i class="fa-solid fa-plus mr-2"/>
				{{ trans('general.create') }}
			</PrimaryButton>
		</template>
		<Card>
			<img :src="storage(`logos/payment-providers/${type}.svg`)" class="h-12 place-self-start" :alt="type">
			<div class="grid grid-cols-1 lg:grid-cols-2 gap-3">
				<LabeledObject required>
					<template #label>{{ trans('events.payments.name') }}</template>
					<IconTextInput v-model="provider.name" icon-class="fa-solid fa-tag" full/>
				</LabeledObject>
				<LabeledObject required>
					<template #label>{{ trans('events.payments.fee') }}</template>
					<NumberInput v-model.number="provider.fee" :step=".01"/>
				</LabeledObject>
				<LabeledObject required>
					<template #label>{{ trans('events.payments.access_token') }}</template>
					<IconTextInput v-model="provider.access_token" icon-class="fa-solid fa-lock" full/>
				</LabeledObject>
				<LabeledObject required>
					<template #label>{{ trans('events.payments.public_key') }}</template>
					<IconTextInput v-model="provider.public_key" icon-class="fa-solid fa-key" full/>
				</LabeledObject>
			</div>
		</Card>
	</EventLayout>
</template>
<script setup lang="ts">
import axios from 'axios'
import {route} from 'ziggy-js'
import {trans} from 'laravel-vue-i18n'
import {Color} from '@/Types/Enums/Color'
import Card from '@/Components/UI/Cards/Card.vue'
import {defineProps, type Ref, ref} from 'vue'
import {type Event} from '@/Types/Events/Event'
import EventLayout from '@/Layouts/EventLayout.vue'
import LabeledObject from '@/Components/UI/LabeledObject.vue'
import NumberInput from '@/Components/UI/Inputs/NumberInput.vue'
import IconTextInput from '@/Components/UI/Inputs/IconTextInput.vue'
import PrimaryButton from '@/Components/UI/Buttons/PrimaryButton.vue'
import {type PaymentProvider} from '@/Types/Events/Payments/PaymentProvider'
import {showInfo, showSuccess, showValidationErrors, storage} from '@/Helpers/general'

const props = defineProps<{
	type:string,
	event:Event,
	provider?:PaymentProvider,
}>()

const type:Ref<string> = ref(props.type)
const event:Ref<Event> = ref(props.event)
const working:Ref<boolean> = ref(false)
const provider:Ref<PaymentProvider> = ref(props.provider ?? {
	id: 0,
	name: '',
	access_token: '',
	public_key: '',
	fee: 0.0,
	type: type.value,
} as PaymentProvider)

function insert():void{
	if(!working.value){
		working.value = true
		showInfo(trans('general.inserting'))
		axios.post(route('events.payments.store', event.value.id), provider.value).then(({data}) => {
			showSuccess(trans('general.inserted'))
			provider.value = data
		}).catch(showValidationErrors).finally(() => {
			working.value = false
		})
	}
}

function update():void{
	if(!working.value){
		working.value = true
		showInfo(trans('general.updating'))
		axios.patch(route('events.payments.update', provider.value.id), provider.value).then(() => {
			showSuccess(trans('general.updated'))
		}).catch(showValidationErrors).finally(() => {
			working.value = false
		})
	}
}

function remove():void{
	if(!working.value && confirm(trans('general.confirm_action'))){
		working.value = true
		showInfo(trans('general.deleting'))
		axios.delete(route('events.payments.delete', provider.value.id)).then(() => {
			showSuccess(trans('general.deleted'))
			provider.value.id = 0
			provider.value.name = ''
			provider.value.access_token = ''
			provider.value.public_key = ''
			provider.value.fee = 0
		}).catch(showValidationErrors).finally(() => {
			working.value = false
		})
	}
}
</script>